import axios from "axios";

import { getCsrfToken } from "../lib/csrf";
import { cacheClearAll } from "../lib/cache";
import { draftClearAll } from "../lib/draftStore";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

const client = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
});

client.interceptors.request.use((config) => {
  const method = config.method?.toLowerCase() || "get";
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

let _clearingSession = false;

// Set by App.jsx (via useNavigate) so a rejected request can trigger an
// in-app SPA navigation instead of a hard reload. Falls back to
// window.location when nothing has registered yet (e.g. outside the React
// tree, or in tests that don't wire it up).
let _navigateToNoLicense = null;

/** @param {(path: string) => void} fn */
export function setLicenseRedirectHandler(fn) {
  _navigateToNoLicense = fn;
}

/**
 * Recognizes the two license-lockout response shapes the backend can send
 * during the subscription→licensing migration window:
 *   - 402 Payment Required (pre-Phase-3 backend, `require_active_subscription`)
 *   - 403 Forbidden with `detail: { code: "license_required", ... }`
 *     (post-Phase-3 backend, `require_active_license`)
 * A plain 403 (permission/RBAC denial, feature-gating, etc.) with a string,
 * array, or missing `detail` is NOT a license error and must not redirect.
 *
 * Status/detail signature (rather than an axios error) so the raw-fetch SSE
 * path in ChatPage.jsx can share the exact same check.
 *
 * @param {number|undefined} status
 * @param {unknown} detail - the response body's `detail` field, any shape
 */
export function isLicenseRequiredStatus(status, detail) {
  if (status === 402) return true;
  if (status === 403 && detail && typeof detail === "object" && !Array.isArray(detail)) {
    return detail.code === "license_required";
  }
  return false;
}

/** Axios-error wrapper around {@link isLicenseRequiredStatus}. */
export function isLicenseRequiredError(error) {
  return isLicenseRequiredStatus(error?.response?.status, error?.response?.data?.detail);
}

function redirectToNoLicense() {
  if (typeof window !== "undefined" && window.location.pathname === "/no-license") {
    return;
  }
  cacheClearAll();
  draftClearAll();
  if (_navigateToNoLicense) {
    _navigateToNoLicense("/no-license");
  } else if (typeof window !== "undefined") {
    window.location.assign("/no-license");
  }
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !_clearingSession) {
      _clearingSession = true;
      setTimeout(() => {
        _clearingSession = false;
      }, 2000);
    }
    if (isLicenseRequiredError(error)) {
      redirectToNoLicense();
    }
    return Promise.reject(error);
  },
);

export default client;
